import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Search, ChevronDown, Briefcase, Euro, FileText, Users } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/pages/Kennisbank";

// Conservatief OCR-herstel (alleen weergave): afbrekingen midden in een woord samenvoegen
// ("ma- chinaal" -> "machinaal"); samentrekkingen vóór "en"/"of" blijven staan
// ("kwaliteits- en", "dag- of"); 1-letterdelen behouden het streepje ("E- systeem" -> "E-systeem").
const herstelAfbrekingen = (t) => (t || "")
  .replace(/([a-zà-üA-Z]{2,})- (?!en\b|of\b)(?=[a-zà-ü])/g, "$1")
  .replace(/\b([a-zA-Z])- (?!en\b|of\b)(?=[a-zà-ü])/g, "$1-");

// Zichtbaar NOK-jargon weglaten uit de afgeleide omschrijving/brontekst (alleen presentatie).
const zonderNokPrefix = (t) => (t || "").replace(/^NOK-matrix .*?\(functiehandboek, pagina \d+\)\.\s*/, "");

// Functiegroep-bron in gewone taal: "…(functiehandboek p. 21)" -> "functiehandboek, pagina 21".
const bronInGewoneTaal = (bron) => {
  const m = (bron || "").match(/functiehandboek p\. (\d+)/);
  return m ? `functiehandboek, pagina ${m[1]}` : (bron || "").replace(/NOK-matrix\s*/gi, "");
};

// Documentkopjes op een eigen regel en vet (tekst blijft volledig behouden).
const KOPJES = [
  "POSITIE IN DE ORGANISATIE", "BEZWARENDE WERKOMSTANDIGHEDEN", "WERKGERELATEERDE BEZWAREN",
  "RESULTAATVERWACHTINGEN", "RESULTAATVERWACHTING", "KERNACTIVITEITEN", "FUNCTIECONTEXT",
  "FUNCTIEDOEL", "SOCIALE INTERACTIE", "SPECIFIEKE HANDELINGSVEREISTEN", "DISCIPLINE",
];
const KOPJE_RE = new RegExp(`(${KOPJES.join("|")})`, "g");

// Opsommingen ("- Hinder van geluid.") op een eigen regel met een bullet;
// alleen streepjes gevolgd door een hoofdletter (afbrekingen zijn al hersteld).
const metBullets = (t) => (t || "")
  .replace(/\s-\s(?=[A-Za-zÀ-ü])/g, "\n• ")
  .replace(/\s+(?=Het functieniveau volgens ORBA)/, "\n\n");

const BronTekst = ({ tekst }) => {
  const netjes = herstelAfbrekingen(zonderNokPrefix(tekst));
  const delen = netjes.split(KOPJE_RE);
  return (
    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
      {delen.map((deel, i) =>
        KOPJES.includes(deel)
          ? <span key={i} className="block font-bold text-slate-800 mt-2">{deel}</span>
          : <span key={i}>{metBullets(deel)}</span>
      )}
    </p>
  );
};

// Variantkenmerken in gewone taal: technische indexerings-preamble alleen bij wéérgave
// overslaan; de bestaande kenmerken ongewijzigd als bulletlijst tonen (geen herschrijving).
const VariantKenmerken = ({ functie }) => {
  const t = herstelAfbrekingen(zonderNokPrefix(functie.brontekst || functie.omschrijving || ""));
  const bullets = t.split("\n").filter((r) => r.trim().startsWith("- ")).map((r) => r.trim().slice(2));
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-semibold text-slate-800">
        Wat maakt {functie.naam} anders dan {functie.referentie ? functie.referentie.naam : "de referentiefunctie"}?
      </p>
      {bullets.length > 0 ? (
        <ul className="text-sm text-slate-700 leading-relaxed list-disc pl-5 space-y-1">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      ) : (
        <p className="text-sm text-slate-700 leading-relaxed">
          {t.replace(/^Niveau onderscheidende kenmerken.*?Letterlijke kenmerken uit de matrix:\s*/s, "")}
        </p>
      )}
    </div>
  );
};

const FunctieKaart = ({ functie, open, onToggle, onFunctiecheck, onSalarischeck }) => {
  const [toonBron, setToonBron] = useState(false);
  const f = functie;
  return (
    <div id={`functie-${f.code}`} data-testid={`handboek-functie-${f.code}`}
      className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button data-testid={`handboek-functie-toggle-${f.code}`} onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
        <span className="font-mono text-sm font-bold text-[#B9772B] shrink-0 w-12">{f.code}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-slate-900">{f.naam}</span>
          {f.werkvloernamen.length > 0 && (
            <span className="block text-xs text-slate-500 truncate">ook bekend als: {f.werkvloernamen.join(", ")}</span>
          )}
        </span>
        {f.functiegroep && (
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide bg-[#FBF3E3] text-[#8a5a20] rounded px-2 py-1">
            Groep {f.functiegroep}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`handboek-functie-detail-${f.code}`} className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100">
          {f.type === "variant" && f.referentie && (
            <p className="text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-md px-3 py-2">
              Deze functie is een variant van <span className="font-semibold">{f.referentie.code} {f.referentie.naam}</span>.
            </p>
          )}
          {f.type === "variant" && (f.brontekst || f.omschrijving) ? (
            <VariantKenmerken functie={f} />
          ) : f.omschrijving ? (
            <p className="text-sm text-slate-700 leading-relaxed">{herstelAfbrekingen(zonderNokPrefix(f.omschrijving))}</p>
          ) : (
            <p className="text-sm text-slate-500 italic">
              Voor deze functie is geen aparte beschrijving opgenomen in het functiehandboek.
              {f.referentie && <> Zie referentiefunctie {f.referentie.code} {f.referentie.naam}.</>}
            </p>
          )}
          {f.praktijk && f.praktijk.length > 0 && (
            <div data-testid={`handboek-praktijk-${f.code}`} className="space-y-1.5">
              <p className="text-sm font-semibold text-slate-800">Wat doe je in deze functie in de praktijk?</p>
              <ul className="text-sm text-slate-700 leading-relaxed list-disc pl-5 space-y-1">
                {f.praktijk.map((p, i) => <li key={i}>{herstelAfbrekingen(p)}</li>)}
              </ul>
            </div>
          )}
          {f.werkvloernamen.length > 0 && (
            <p data-testid={`handboek-werkvloernamen-${f.code}`} className="text-xs text-slate-500">
              Op de werkvloer ook bekend als: {f.werkvloernamen.join(", ")}.
            </p>
          )}
          {f.functiegroep && f.functiegroep_bron && (
            <p className="text-xs text-slate-500">
              Functiegroep {f.functiegroep} — bron: {bronInGewoneTaal(f.functiegroep_bron)}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid={`handboek-functiecheck-btn-${f.code}`} size="sm"
              onClick={() => onFunctiecheck(f)} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Start Functiecheck
            </Button>
            <Button data-testid={`handboek-salarischeck-btn-${f.code}`} size="sm" variant="outline"
              onClick={() => onSalarischeck(f)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Euro className="w-3.5 h-3.5 mr-1.5" /> Salarischeck
            </Button>
            {f.brontekst && (
              <Button data-testid={`handboek-bron-btn-${f.code}`} size="sm" variant="outline"
                onClick={() => setToonBron(!toonBron)} className="border-slate-300 text-slate-600">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> {toonBron ? "Verberg bron" : "Waar staat dat?"}
              </Button>
            )}
          </div>
          {toonBron && f.brontekst && (
            <div data-testid={`handboek-brontekst-${f.code}`} className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Bron: Functiehandboek Zoetwarenindustrie{f.pagina ? `, pagina ${f.pagina}` : ""}
              </p>
              <BronTekst tekst={f.brontekst} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Takencheck voor brede werkvloernamen (geaccordeerd 2026-07): gewone werkvloertaal,
// geen ORBA-jargon. Eenduidige termen (transporteur, heftruckchauffeur) vallen hier bewust buiten.
const TAKENCHECKS = [
  { termen: ["vormer", "vormmachine", "vormlijn", "machineman", "machinebediener", "productieoperator", "lijnoperator", "operator productie"],
    regels: [
      "Bedien je één machine (of een paar), werk je volgens instructie en wissel of controleer je vooral? → Medewerker productie B",
      "Werk je aan een (vorm)lijn of een deel van de productielijn, start je zelf op, bouw je om, meet je de kwaliteit en los je kleine storingen zelf op? → Operator productie A",
      "Draai je meerdere machines of de hele lijn, bewaak je het totale proces, los je zwaardere storingen op en geef je aanwijzingen aan de lijn? → Operator productie B",
    ] },
  { termen: ["intern transport", "magazijntransport", "truckchauffeur", "palletchauffeur"],
    regels: [
      "Verplaats je goederen vooral met een palletwagen en doe je vaste, terugkerende handelingen? → Medewerker intern transport A",
      "Rijd je heftruck of reachtruck (met certificaat), laad en los je vrachtwagens en schrijf je verplaatsingen op bonnen of lijsten? → Medewerker intern transport B",
      "Werk je ook met een scanner of registratiesysteem, bepaal je zelf de volgorde van je werk of doe je bredere magazijntaken? → Medewerker magazijn A of B",
    ] },
  { termen: ["monteur", "onderhoudsmonteur", "technische dienst", "td monteur", "td-monteur"],
    uitgezonderd: ["storingsmonteur", "elektromonteur", "mechanisch monteur", "monteur a", "monteur b", "technisch specialist"],
    regels: [
      "Doe je vooral mechanisch onderhoud en reparaties (lagers, aandrijvingen, leidingwerk, lassen) en los je mechanische storingen op? → Monteur A",
      "Werk je ook aan elektrotechniek of besturing (schakelkasten, elektrische circuits, PLC- en meet- en regeltechniek) en zoek je zelfstandig storingen? → Monteur B",
      "Analyseer je complexe storingen en trends, doe je specialistische metingen, programmeer of regel je installaties in en werk je verbetervoorstellen uit? → Technisch specialist E&I",
    ] },
];

const Functiehandboek = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(false);
  const [zoek, setZoek] = useState(searchParams.get("zoek") || "");
  const [openDisciplines, setOpenDisciplines] = useState(() => new Set());
  const [openFunctie, setOpenFunctie] = useState(searchParams.get("functie") || null);
  const scrolled = useRef(false);

  useEffect(() => {
    api.get("/functiehandboek").then((res) => setData(res.data)).catch(() => setFout(true));
  }, []);

  // Deep-link ?functie=10.06: open discipline + functie en scroll ernaartoe.
  useEffect(() => {
    const code = searchParams.get("functie");
    if (data && code && !scrolled.current) {
      scrolled.current = true;
      setOpenDisciplines((prev) => new Set([...prev, code.split(".")[0]]));
      setOpenFunctie(code);
      setTimeout(() => document.getElementById(`functie-${code}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [data, searchParams]);

  const q = zoek.trim().toLowerCase();
  const gefilterd = useMemo(() => {
    if (!data) return [];
    if (!q) return data.disciplines;
    // Spatie-tolerant: "hulp menger" vindt "hulpmenger" (compacte vorm vergelijken).
    const qc = q.replace(/\s+/g, "");
    return data.disciplines
      .map((d) => ({ ...d, functies: d.functies.filter((f) => f.zoektermen.some((t) => t.includes(q) || t.replace(/\s+/g, "").includes(qc))) }))
      .filter((d) => d.functies.length > 0);
  }, [data, q]);

  const takencheck = useMemo(() => {
    if (!q || q.length < 4) return null;
    // Woordgrens: "heftruckchauffeur" (eenduidig 30.02) mag niet matchen op "truckchauffeur".
    // Uitgezonderd: eenduidige samenstellingen (bijv. "elektromonteur") tonen geen takencheck.
    return TAKENCHECKS.find((t) => !(t.uitgezonderd || []).some((u) => q.includes(u)) &&
      t.termen.some((term) => term.includes(q) || new RegExp(`(^|[^a-z])${term}`).test(q))) || null;
  }, [q]);

  const toggleDiscipline = (code) => setOpenDisciplines((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });

  const naarFunctiecheck = (f) => navigate("/functie", { state: { functienaam: f.naam } });
  const naarSalarischeck = (f) => navigate("/salaris", f.functiegroep ? { state: { functiegroep: f.functiegroep } } : undefined);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Functiehandboek" icon={BookOpen}
        accent={{ chip: "bg-[#B9772B]", bar: "from-[#D19A4E] to-[#8a5a20]", text: "text-[#8a5a20]" }}
        subtitle="Alle officiële functies uit het Functiehandboek Zoetwarenindustrie, per discipline. Zoek op functiecode, officiële functienaam of werkvloernaam." />

      <div className="max-w-3xl">
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="handboek-zoek-input" value={zoek} onChange={(e) => setZoek(e.target.value)}
            placeholder="Bijv. 10.06, menger, heftruckchauffeur of teamleider…"
            className="pl-9 bg-white border-slate-300 rounded-lg shadow-sm focus-visible:border-[#B9772B] focus-visible:ring-2 focus-visible:ring-[#B9772B]/40 focus-visible:ring-offset-0" />
        </div>
        <p className="text-xs text-slate-500 mb-5" data-testid="handboek-zoek-hulplijn">
          Zoek hier op een functiecode, functienaam of werkvloernaam. Voor langere vragen gebruik je{" "}
          <Link to="/" className="text-[#B9772B] font-medium hover:underline">Stel een vraag</Link>.
        </p>

        {fout && (
          <p data-testid="handboek-fout" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            Het functiehandboek kon niet worden geladen. Probeer het later opnieuw.
          </p>
        )}
        {!data && !fout && <p className="text-sm text-slate-400">Functiehandboek laden…</p>}

        {data && (
          <>
            <p data-testid="handboek-teller" className="text-xs text-slate-500 mb-3">
              {q
                ? `${gefilterd.reduce((n, d) => n + d.functies.length, 0)} functies gevonden`
                : `${data.totaal_functies} officiële functies (${data.totaal_referentiefuncties} referentiefuncties en ${data.totaal_varianten} A/C-varianten) in 7 disciplines`}
            </p>
            {takencheck && (
              <div data-testid="handboek-takencheck" className="mb-4 bg-[#FDF8EE] border border-[#EBD9BC] rounded-lg p-3.5 space-y-1.5">
                <p className="text-sm font-semibold text-[#8a5a20]">Deze werkvloernaam past bij meerdere officiële functies</p>
                <p className="text-xs text-slate-600">Check je taken om te zien welke functie het beste past:</p>
                <ul className="space-y-1.5">
                  {takencheck.regels.map((r, i) => (
                    <li key={i} className="text-xs text-slate-700 leading-relaxed flex gap-1.5">
                      <span className="text-[#B9772B] shrink-0">•</span><span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {q && gefilterd.length === 0 && (
              <p data-testid="handboek-geen-resultaat" className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-6 text-center">
                Geen functies gevonden voor "{zoek}". Twijfel je over je functie? Doe dan de{" "}
                <button onClick={() => navigate("/functie")} className="underline font-medium text-amber-700">Functiecheck</button>.
              </p>
            )}
            <div className="space-y-3">
              {gefilterd.map((d) => {
                const open = q ? true : openDisciplines.has(d.code);
                return (
                  <div key={d.code} data-testid={`handboek-discipline-${d.code}`} className="zk-card overflow-hidden">
                    <button data-testid={`handboek-discipline-toggle-${d.code}`} onClick={() => toggleDiscipline(d.code)}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#FBF3E3]/50 transition-colors">
                      <span className="w-9 h-9 rounded-lg zk-brand-gradient flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4 text-white" />
                      </span>
                      <span className="flex-1 font-heading font-bold text-[#3A281B]">
                        Discipline {d.code} — {d.naam}
                      </span>
                      <span className="text-xs text-slate-500 shrink-0">{d.functies.length} functies</span>
                      {!q && <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />}
                    </button>
                    {open && (
                      <div className="px-3 pb-3 space-y-2">
                        {d.functies.map((f) => (
                          <FunctieKaart key={f.code} functie={f} open={openFunctie === f.code}
                            onToggle={() => setOpenFunctie(openFunctie === f.code ? null : f.code)}
                            onFunctiecheck={naarFunctiecheck} onSalarischeck={naarSalarischeck} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-5 leading-relaxed">
              Bron: {data.bron.replace(" (ORBA)", "")}. Dit overzicht is een naslagwerk en geen indeling van jouw persoonlijke situatie.
              Twijfel je of jouw werkzaamheden passen bij je functie-indeling? Gebruik dan de Functiecheck.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Functiehandboek;
